import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function AgentBanking() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [floatBalance, setFloatBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // PIN status
  const [pinStatus, setPinStatus] = useState(null);
  const [pinForm, setPinForm] = useState({ pin: "", confirmPin: "" });
  const [changePinForm, setChangePinForm] = useState({ currentPin: "", newPin: "", confirmNewPin: "" });
  const [pinLoading, setPinLoading] = useState(false);
  
  // Customer search
  const [customerSearch, setCustomerSearch] = useState({ type: "account", value: "" });
  const [customer, setCustomer] = useState(null);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  
  // Transaction forms
  const [depositForm, setDepositForm] = useState({ amount: "", description: "", pin: "" });
  const [withdrawForm, setWithdrawForm] = useState({ amount: "", description: "", pin: "" });
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionPin, setActionPin] = useState("");

  // Statement state (simplified - agent's own statement)
  const [statementForm, setStatementForm] = useState({ startDate: "", endDate: "" });
  const [downloadingStatement, setDownloadingStatement] = useState(false);
  const [statementError, setStatementError] = useState("");
  const [statementSuccess, setStatementSuccess] = useState("");

  // Using centralized api from services/api.js - auto-detects environment

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [floatRes, txRes] = await Promise.all([
        api.get("/Wallet/agent/float").catch(() => ({ data: { currentBalance: 0 } })),
        api.get("/Transaction/my-transactions?limit=20").catch(() => ({ data: { transactions: [] } }))
      ]);
      setFloatBalance(floatRes.data.currentBalance || floatRes.data.balance || 0);
      setTransactions(txRes.data.transactions || txRes.data || []);
      
      try {
        const summaryRes = await api.get("/Wallet/agent/daily-summary");
        setDailySummary(summaryRes.data);
      } catch (e) {}
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load PIN status
  const loadPinStatus = async () => {
    try {
      const res = await api.get("/TwoFactor/status");
      setPinStatus(res.data);
    } catch (err) {
      console.error("Failed to load PIN status:", err);
      setPinStatus({ isEnabled: false });
    }
  };

  useEffect(() => {
    const checkAgentAccess = async () => {
      try {
        const res = await api.get("/Auth/me");
        const userRole = (res.data.role || "").toLowerCase();
        const isAdmin = userRole === "admin" || res.data.isAdmin === true;
        
        if (!res.data.isAgent) {
          if (isAdmin) {
            navigate("/admin");
          } else {
            navigate("/user-banking");
          }
          return;
        }
        setUser(res.data);
        loadDashboardData();
        loadPinStatus();
      } catch (err) {
        navigate("/agent-login");
      }
    };
    
    checkAgentAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize statement dates (default last 30 days)
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setStatementForm({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
  }, []);

  // Debounce function for auto-search
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Auto-search customer function
  const searchCustomerAuto = async (value, type) => {
    if (!value) {
      setCustomer(null);
      return;
    }

    // Validate input before searching
    if (type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setCustomer(null);
        return;
      }
    } else {
      // Account number should be at least 10 digits
      if (value.length < 10) {
        setCustomer(null);
        return;
      }
    }

    setSearchingCustomer(true);
    try {
      const params = type === "email" 
        ? { email: value }
        : { accountNumber: value };
      const res = await api.get("/Wallet/agent/search-customer", { params });
      setCustomer(res.data);
    } catch (err) {
      setCustomer(null);
    } finally {
      setSearchingCustomer(false);
    }
  };

  // Debounced search - waits 500ms after user stops typing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value, type) => {
      searchCustomerAuto(value, type);
    }, 500),
    []
  );

  // Handle customer search input change
  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch({ ...customerSearch, value });
    setCustomer(null);
    debouncedSearch(value, customerSearch.type);
  };

  // Handle search type change
  const handleSearchTypeChange = (type) => {
    setCustomerSearch({ type, value: "" });
    setCustomer(null);
  };

  // Check PIN status before deposit
  const initiateDeposit = () => {
    if (!pinStatus?.isEnabled) {
      alert("Please set up your Transaction PIN first in Settings");
      setActiveTab("settings");
      return;
    }
    if (!customer || !depositForm.amount || Number(depositForm.amount) <= 0) {
      alert("Please search for customer and enter a valid amount");
      return;
    }
    setPendingAction("deposit");
    setShowPinModal(true);
  };

  // Check PIN status before withdraw
  const initiateWithdraw = () => {
    if (!pinStatus?.isEnabled) {
      alert("Please set up your Transaction PIN first in Settings");
      setActiveTab("settings");
      return;
    }
    if (!customer || !withdrawForm.amount || Number(withdrawForm.amount) <= 0) {
      alert("Please search for customer and enter a valid amount");
      return;
    }
    if (Number(withdrawForm.amount) > customer.balance) {
      alert("Customer has insufficient balance");
      return;
    }
    if (Number(withdrawForm.amount) > floatBalance) {
      alert("Insufficient float balance");
      return;
    }
    setPendingAction("withdraw");
    setShowPinModal(true);
  };

  const executeDeposit = async () => {
    try {
      await api.post("/Wallet/agent/deposit", {
        customerAccountNumber: customer.accountNumber,
        amount: Number(depositForm.amount),
        description: depositForm.description || "Cash deposit",
        pin: actionPin
      });
      
      alert(`✅ Deposit Successful!\n\nCustomer: ${customer.fullName}\nAmount: ₦${Number(depositForm.amount).toLocaleString()}\nDescription: ${depositForm.description || "Cash deposit"}`);
      
      resetForms();
      loadDashboardData();
    } catch (err) {
      alert(err.response?.data?.message || "Deposit failed");
    }
  };

  const executeWithdraw = async () => {
    try {
      await api.post("/Wallet/agent/withdrawal", {
        customerAccountNumber: customer.accountNumber,
        amount: Number(withdrawForm.amount),
        description: withdrawForm.description || "Cash withdrawal",
        pin: actionPin
      });
      
      alert(`✅ Withdrawal Successful!\n\nCustomer: ${customer.fullName}\nAmount: ₦${Number(withdrawForm.amount).toLocaleString()}\nDescription: ${withdrawForm.description || "Cash withdrawal"}`);
      
      resetForms();
      loadDashboardData();
    } catch (err) {
      alert(err.response?.data?.message || "Withdrawal failed");
    }
  };

  const handlePinSubmit = () => {
    if (!actionPin || actionPin.length < 4) {
      alert("Please enter your PIN");
      return;
    }
    setShowPinModal(false);
    if (pendingAction === "deposit") {
      executeDeposit();
    } else if (pendingAction === "withdraw") {
      executeWithdraw();
    }
    setActionPin("");
    setPendingAction(null);
  };

  const resetForms = () => {
    setShowPinModal(false);
    setActionPin("");
    setPendingAction(null);
    setDepositForm({ amount: "", description: "", pin: "" });
    setWithdrawForm({ amount: "", description: "", pin: "" });
    setCustomer(null);
    setCustomerSearch({ type: "account", value: "" });
    setActiveTab("dashboard");
  };

  // Setup PIN function
  const setupPin = async () => {
    if (!pinForm.pin || pinForm.pin.length < 4 || pinForm.pin.length > 6) {
      alert("PIN must be 4-6 digits");
      return;
    }
    if (pinForm.pin !== pinForm.confirmPin) {
      alert("PINs do not match");
      return;
    }
    if (!/^\d+$/.test(pinForm.pin)) {
      alert("PIN must contain only numbers");
      return;
    }

    setPinLoading(true);
    try {
      await api.post("/TwoFactor/setup", {
        pin: pinForm.pin,
        confirmPin: pinForm.confirmPin
      });
      alert("Transaction PIN set up successfully!");
      setPinForm({ pin: "", confirmPin: "" });
      loadPinStatus();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to set up PIN");
    } finally {
      setPinLoading(false);
    }
  };

  // ========== Statement Functions ==========
  const quickSelectPeriod = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStatementForm({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
    setStatementError("");
    setStatementSuccess("");
  };

  const downloadStatement = async () => {
    setStatementError("");
    setStatementSuccess("");

    if (!statementForm.startDate || !statementForm.endDate) {
      setStatementError("Please select both start and end dates");
      return;
    }

    const start = new Date(statementForm.startDate);
    const end = new Date(statementForm.endDate);

    if (start > end) {
      setStatementError("Start date cannot be after end date");
      return;
    }

    const daysDiff = (end - start) / (1000 * 60 * 60 * 24);
    if (daysDiff > 365) {
      setStatementError("Date range cannot exceed 1 year");
      return;
    }

    setDownloadingStatement(true);

    try {
      const response = await api.get("/Statement/download", {
        params: { startDate: statementForm.startDate, endDate: statementForm.endDate },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Statement_${statementForm.startDate}_${statementForm.endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setStatementSuccess("Statement downloaded successfully!");
    } catch (err) {
      console.error("Download error:", err);
      setStatementError(err.response?.data?.message || "Failed to download statement. Please try again.");
    } finally {
      setDownloadingStatement(false);
    }
  };
  // =========================================

  // Change PIN function
  const changePin = async () => {
    if (!changePinForm.currentPin) {
      alert("Please enter your current PIN");
      return;
    }
    if (!changePinForm.newPin || changePinForm.newPin.length < 4 || changePinForm.newPin.length > 6) {
      alert("New PIN must be 4-6 digits");
      return;
    }
    if (changePinForm.newPin !== changePinForm.confirmNewPin) {
      alert("New PINs do not match");
      return;
    }
    if (!/^\d+$/.test(changePinForm.newPin)) {
      alert("PIN must contain only numbers");
      return;
    }

    setPinLoading(true);
    try {
      await api.post("/TwoFactor/change", {
        currentPin: changePinForm.currentPin,
        newPin: changePinForm.newPin,
        confirmNewPin: changePinForm.confirmNewPin
      });
      alert("Transaction PIN changed successfully!");
      setChangePinForm({ currentPin: "", newPin: "", confirmNewPin: "" });
    } catch (err) {
      alert(err.response?.data?.message || "Failed to change PIN");
    } finally {
      setPinLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/agent-login");
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  const formatDate = (date) => new Date(date).toLocaleDateString("en-NG", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  if (loading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingSpinner}></div>
        <p>Loading agent portal...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>Uptech Banking</h1>
          <span style={styles.agentBadge}>Agent Portal</span>
          {!pinStatus?.isEnabled && (
            <span style={styles.pinWarningBadge}>PIN Not Set</span>
          )}
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcomeText}>Agent: {user?.fullName}</span>
          <button onClick={logout} style={styles.logoutBtn}>Logout</button>
        </div>
      </header>

      {/* Navigation */}
      <nav style={styles.nav}>
        {[
          { id: "dashboard", label: "🏠 Dashboard" },
          { id: "deposit", label: "💵 Cash Deposit" },
          { id: "withdraw", label: "💸 Cash Withdrawal" },
          { id: "statement", label: "📄 Statement" },
          { id: "transactions", label: "📋 Transactions" },
          { id: "settings", label: "⚙️ Settings" }
        ].map(item => (
          <button
            key={item.id}
            onClick={() => { setActiveTab(item.id); setCustomer(null); setCustomerSearch({ type: "account", value: "" }); }}
            style={{ ...styles.navItem, ...(activeTab === item.id ? styles.navItemActive : {}) }}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <main style={styles.main}>
        {/* PIN Alert Banner */}
        {!pinStatus?.isEnabled && activeTab !== "settings" && (
          <div style={styles.pinAlert}>
            <strong>Transaction PIN Required:</strong> You must set up your transaction PIN before you can process deposits and withdrawals.
            <button onClick={() => setActiveTab("settings")} style={styles.pinAlertBtn}>
              Set Up PIN Now
            </button>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div style={styles.dashboard}>
            <div style={styles.floatCard}>
              <div style={styles.floatCardHeader}>
                <span style={styles.floatLabel}>Agent Float Balance</span>
                <span style={styles.floatStatus}>● Active</span>
              </div>
              <div style={styles.floatBalance}>{formatCurrency(floatBalance)}</div>
              <div style={styles.floatInfo}>Available for customer withdrawals</div>
            </div>

            {dailySummary && (
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statIcon}>💰</div>
                  <div style={styles.statInfo}>
                    <div style={styles.statValue}>{formatCurrency(dailySummary.totalDeposits)}</div>
                    <div style={styles.statLabel}>Today's Deposits</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statIcon}>💸</div>
                  <div style={styles.statInfo}>
                    <div style={styles.statValue}>{formatCurrency(dailySummary.totalWithdrawals)}</div>
                    <div style={styles.statLabel}>Today's Withdrawals</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statIcon}>📊</div>
                  <div style={styles.statInfo}>
                    <div style={styles.statValue}>{dailySummary.transactionCount}</div>
                    <div style={styles.statLabel}>Transactions</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statIcon}>💵</div>
                  <div style={styles.statInfo}>
                    <div style={styles.statValue}>{formatCurrency(dailySummary.commission)}</div>
                    <div style={styles.statLabel}>Commission</div>
                  </div>
                </div>
              </div>
            )}

            <div style={styles.quickActions}>
              <h3 style={styles.sectionTitle}>Quick Actions</h3>
              <div style={styles.actionGrid}>
                <button onClick={() => setActiveTab("deposit")} style={{...styles.actionBtn, backgroundColor: "#d4edda", color: "#155724"}}>
                  <span style={styles.actionIcon}>💵</span>
                  Cash Deposit
                </button>
                <button onClick={() => setActiveTab("withdraw")} style={{...styles.actionBtn, backgroundColor: "#f8d7da", color: "#721c24"}}>
                  <span style={styles.actionIcon}>💸</span>
                  Cash Withdrawal
                </button>
                <button onClick={() => navigate("/agent/account-opening")} style={{...styles.actionBtn, backgroundColor: "#e8f5e9", color: "#2e7d32"}}>
                  <span style={styles.actionIcon}>🏦</span>
                  Open Account
                </button>
                <button onClick={() => setActiveTab("transactions")} style={{...styles.actionBtn, backgroundColor: "#e3f2fd", color: "#1565c0"}}>
                  <span style={styles.actionIcon}>📋</span>
                  Transactions
                </button>
              </div>
            </div>

            <div style={styles.recentSection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Recent Transactions</h3>
                <button onClick={() => setActiveTab("transactions")} style={styles.viewAllBtn}>View All →</button>
              </div>
              <div style={styles.transactionList}>
                {transactions.length > 0 ? transactions.slice(0, 5).map((tx, i) => (
                  <div key={i} style={styles.transactionItem}>
                    <div style={styles.txLeft}>
                      <span style={{
                        ...styles.txIcon,
                        backgroundColor: tx.type?.includes("Deposit") ? "#d4edda" : "#f8d7da"
                      }}>
                        {tx.type?.includes("Deposit") ? "↓" : "↑"}
                      </span>
                      <div>
                        <div style={styles.txDescription}>{tx.description}</div>
                        <div style={styles.txDate}>{formatDate(tx.createdAt)}</div>
                      </div>
                    </div>
                    <div style={{
                      ...styles.txAmount,
                      color: tx.type?.includes("Deposit") ? "#28a745" : "#dc3545"
                    }}>
                      {formatCurrency(Math.abs(tx.amount))}
                    </div>
                  </div>
                )) : (
                  <div style={styles.noData}>No recent transactions</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Deposit Tab */}
        {activeTab === "deposit" && (
          <div style={styles.transactionSection}>
            <h2 style={styles.pageTitle}>Cash Deposit</h2>
            <p style={styles.pageSubtitle}>Deposit cash for customers</p>

            <div style={styles.transactionCard}>
              <div style={styles.searchSection}>
                <h4 style={styles.formTitle}>Step 1: Find Customer</h4>
                <div style={styles.searchTypeRow}>
                  <label style={styles.radioLabel}>
                    <input 
                      type="radio" 
                      checked={customerSearch.type === "account"} 
                      onChange={() => handleSearchTypeChange("account")} 
                    />
                    Account Number
                  </label>
                  <label style={styles.radioLabel}>
                    <input 
                      type="radio" 
                      checked={customerSearch.type === "email"} 
                      onChange={() => handleSearchTypeChange("email")} 
                    />
                    Email
                  </label>
                </div>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    value={customerSearch.value}
                    onChange={handleCustomerSearchChange}
                    placeholder={customerSearch.type === "email" ? "Enter email address" : "Enter 10-digit account number"}
                    style={styles.input}
                  />
                  {searchingCustomer && (
                    <div style={styles.searchingIndicator}>
                      <div style={styles.spinner}></div>
                    </div>
                  )}
                </div>
                <p style={styles.helperText}>
                  {customerSearch.type === "email" 
                    ? "Customer will be found automatically when you enter a valid email" 
                    : "Customer will be found automatically when you enter a valid account number"}
                </p>
              </div>

              {customer && (
                <>
                  <div style={styles.customerCard}>
                    <div style={styles.customerIcon}>{customer.fullName?.charAt(0)?.toUpperCase() || "U"}</div>
                    <div style={styles.customerDetails}>
                      <div style={styles.customerName}>{customer.fullName}</div>
                      <div style={styles.customerAccount}>{customer.accountNumber}</div>
                      <div style={styles.customerVerified}>✓ Verified Customer</div>
                    </div>
                  </div>

                  <div style={styles.amountSection}>
                    <h4 style={styles.formTitle}>Step 2: Enter Amount</h4>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Amount (NGN)</label>
                      <input
                        type="number"
                        value={depositForm.amount}
                        onChange={(e) => setDepositForm({...depositForm, amount: e.target.value})}
                        placeholder="0.00"
                        style={styles.amountInput}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Description (Optional)</label>
                      <input
                        type="text"
                        value={depositForm.description}
                        onChange={(e) => setDepositForm({...depositForm, description: e.target.value})}
                        placeholder="Cash deposit"
                        style={styles.input}
                      />
                    </div>
                    <button 
                      onClick={initiateDeposit} 
                      style={{
                        ...styles.actionButton, 
                        backgroundColor: "#28a745",
                        opacity: !depositForm.amount ? 0.6 : 1,
                        cursor: !depositForm.amount ? "not-allowed" : "pointer"
                      }}
                      disabled={!depositForm.amount}
                    >
                      Process Deposit
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Withdraw Tab */}
        {activeTab === "withdraw" && (
          <div style={styles.transactionSection}>
            <h2 style={styles.pageTitle}>Cash Withdrawal</h2>
            <p style={styles.pageSubtitle}>Process cash withdrawals for customers</p>

            {floatBalance < 1000 && (
              <div style={styles.floatWarning}>
                Low Float Balance: {formatCurrency(floatBalance)} - Request float top-up from admin
              </div>
            )}

            <div style={styles.transactionCard}>
              <div style={styles.searchSection}>
                <h4 style={styles.formTitle}>Step 1: Find Customer</h4>
                <div style={styles.searchTypeRow}>
                  <label style={styles.radioLabel}>
                    <input 
                      type="radio" 
                      checked={customerSearch.type === "account"} 
                      onChange={() => handleSearchTypeChange("account")} 
                    />
                    Account Number
                  </label>
                  <label style={styles.radioLabel}>
                    <input 
                      type="radio" 
                      checked={customerSearch.type === "email"} 
                      onChange={() => handleSearchTypeChange("email")} 
                    />
                    Email
                  </label>
                </div>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    value={customerSearch.value}
                    onChange={handleCustomerSearchChange}
                    placeholder={customerSearch.type === "email" ? "Enter email address" : "Enter 10-digit account number"}
                    style={styles.input}
                  />
                  {searchingCustomer && (
                    <div style={styles.searchingIndicator}>
                      <div style={styles.spinner}></div>
                    </div>
                  )}
                </div>
                <p style={styles.helperText}>
                  {customerSearch.type === "email" 
                    ? "Customer will be found automatically when you enter a valid email" 
                    : "Customer will be found automatically when you enter a valid account number"}
                </p>
              </div>

              {customer && (
                <>
                  <div style={styles.customerCard}>
                    <div style={styles.customerIcon}>{customer.fullName?.charAt(0)?.toUpperCase() || "U"}</div>
                    <div style={styles.customerDetails}>
                      <div style={styles.customerName}>{customer.fullName}</div>
                      <div style={styles.customerAccount}>{customer.accountNumber}</div>
                      <div style={styles.customerVerified}>✓ Verified Customer</div>
                    </div>
                  </div>

                  <div style={styles.amountSection}>
                    <h4 style={styles.formTitle}>Step 2: Enter Amount</h4>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Amount (NGN)</label>
                      <input
                        type="number"
                        value={withdrawForm.amount}
                        onChange={(e) => setWithdrawForm({...withdrawForm, amount: e.target.value})}
                        placeholder="0.00"
                        style={styles.amountInput}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Description (Optional)</label>
                      <input
                        type="text"
                        value={withdrawForm.description}
                        onChange={(e) => setWithdrawForm({...withdrawForm, description: e.target.value})}
                        placeholder="Cash withdrawal"
                        style={styles.input}
                      />
                    </div>
                    <button 
                      onClick={initiateWithdraw} 
                      style={{
                        ...styles.actionButton, 
                        backgroundColor: "#dc3545",
                        opacity: !withdrawForm.amount ? 0.6 : 1,
                        cursor: !withdrawForm.amount ? "not-allowed" : "pointer"
                      }}
                      disabled={!withdrawForm.amount}
                    >
                      Process Withdrawal
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Statement Tab - Agent's Own Statement */}
        {activeTab === "statement" && (
          <div style={styles.transactionSection}>
            <h2 style={styles.pageTitle}>📄 Account Statement</h2>
            <p style={styles.pageSubtitle}>Download your transaction history as a PDF document</p>

            <div style={styles.transactionCard}>
              {/* Error/Success Messages */}
              {statementError && (
                <div style={{ backgroundColor: "#f8d7da", color: "#721c24", padding: "12px 15px", borderRadius: "8px", marginBottom: "20px" }}>
                  ⚠️ {statementError}
                </div>
              )}
              {statementSuccess && (
                <div style={{ backgroundColor: "#d4edda", color: "#155724", padding: "12px 15px", borderRadius: "8px", marginBottom: "20px" }}>
                  ✅ {statementSuccess}
                </div>
              )}

              {/* Quick Select */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Quick Select Period:</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "8px" }}>
                  <button type="button" onClick={() => quickSelectPeriod(7)} style={styles.quickSelectBtn}>Last 7 Days</button>
                  <button type="button" onClick={() => quickSelectPeriod(30)} style={styles.quickSelectBtn}>Last 30 Days</button>
                  <button type="button" onClick={() => quickSelectPeriod(90)} style={styles.quickSelectBtn}>Last 3 Months</button>
                  <button type="button" onClick={() => quickSelectPeriod(180)} style={styles.quickSelectBtn}>Last 6 Months</button>
                  <button type="button" onClick={() => quickSelectPeriod(365)} style={styles.quickSelectBtn}>Last Year</button>
                </div>
              </div>

              {/* Date Inputs */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px", marginTop: "20px" }}>
                <div>
                  <label style={styles.label}>Start Date</label>
                  <input 
                    type="date" 
                    value={statementForm.startDate} 
                    onChange={(e) => { setStatementForm({ ...statementForm, startDate: e.target.value }); setStatementError(""); setStatementSuccess(""); }} 
                    max={statementForm.endDate || new Date().toISOString().split('T')[0]}
                    style={styles.input} 
                  />
                </div>
                <div>
                  <label style={styles.label}>End Date</label>
                  <input 
                    type="date" 
                    value={statementForm.endDate} 
                    onChange={(e) => { setStatementForm({ ...statementForm, endDate: e.target.value }); setStatementError(""); setStatementSuccess(""); }} 
                    min={statementForm.startDate}
                    max={new Date().toISOString().split('T')[0]} 
                    style={styles.input} 
                  />
                </div>
              </div>

              {/* Download Button */}
              <button
                onClick={downloadStatement}
                disabled={downloadingStatement}
                style={{
                  ...styles.actionButton,
                  backgroundColor: "#007bff",
                  opacity: downloadingStatement ? 0.6 : 1,
                  cursor: downloadingStatement ? "not-allowed" : "pointer"
                }}
              >
                {downloadingStatement ? "⏳ Generating PDF..." : "📥 Download Statement"}
              </button>

              <p style={{ marginTop: "15px", fontSize: "12px", color: "#888", textAlign: "center" }}>
                Your statement will include all transactions within the selected date range, along with opening and closing balances.
              </p>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === "transactions" && (
          <div style={styles.transactionsSection}>
            <h2 style={styles.pageTitle}>Transaction History</h2>
            <p style={styles.pageSubtitle}>View all your agent transactions</p>

            <div style={styles.transactionsCard}>
              {transactions.length > 0 ? (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Description</th>
                      <th style={styles.th}>Type</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, i) => (
                      <tr key={i}>
                        <td style={styles.td}>{formatDate(tx.createdAt)}</td>
                        <td style={styles.td}>{tx.description}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.typeBadge,
                            backgroundColor: tx.type?.includes("Deposit") ? "#d4edda" : "#f8d7da",
                            color: tx.type?.includes("Deposit") ? "#155724" : "#721c24"
                          }}>
                            {tx.type}
                          </span>
                        </td>
                        <td style={{
                          ...styles.td,
                          textAlign: "right",
                          fontWeight: "600",
                          color: tx.type?.includes("Deposit") ? "#28a745" : "#dc3545"
                        }}>
                          {formatCurrency(Math.abs(tx.amount))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={styles.noData}>No transactions found</div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div style={{ maxWidth: "600px", margin: "0 auto" }}>
            <h2 style={styles.pageTitle}>Settings</h2>
            <p style={styles.pageSubtitle}>Manage your account settings and security</p>

            {/* PIN Settings Card */}
            <div style={styles.settingsCard}>
              <h3 style={styles.settingsCardTitle}>Transaction PIN</h3>
              
              <div style={styles.pinStatusBox}>
                <div style={styles.pinStatusRow}>
                  <span>PIN Status:</span>
                  <span style={{
                    ...styles.pinStatusBadge,
                    backgroundColor: pinStatus?.isEnabled ? "#d4edda" : "#f8d7da",
                    color: pinStatus?.isEnabled ? "#155724" : "#721c24"
                  }}>
                    {pinStatus?.isEnabled ? "Enabled" : "Not Set"}
                  </span>
                </div>
                {pinStatus?.isLocked && (
                  <div style={styles.lockedWarning}>Account is locked due to too many failed attempts. Contact admin.</div>
                )}
              </div>

              {/* Setup PIN Form (if not enabled) */}
              {!pinStatus?.isEnabled && (
                <div style={styles.pinSetupSection}>
                  <h4 style={styles.pinSetupTitle}>Set Up Your Transaction PIN</h4>
                  <p style={styles.pinSetupDesc}>Create a 4-6 digit PIN to authorize transactions.</p>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Enter PIN (4-6 digits)</label>
                    <input
                      type="password"
                      value={pinForm.pin}
                      onChange={(e) => setPinForm({...pinForm, pin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="Enter PIN"
                      maxLength={6}
                      style={styles.pinInputField}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Confirm PIN</label>
                    <input
                      type="password"
                      value={pinForm.confirmPin}
                      onChange={(e) => setPinForm({...pinForm, confirmPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="Confirm PIN"
                      maxLength={6}
                      style={styles.pinInputField}
                    />
                  </div>
                  <button 
                    onClick={setupPin} 
                    style={{...styles.pinSetupBtn, opacity: pinLoading ? 0.6 : 1}}
                    disabled={pinLoading}
                  >
                    {pinLoading ? "Setting up..." : "Set Up PIN"}
                  </button>
                </div>
              )}

              {/* Change PIN Form (if enabled) */}
              {pinStatus?.isEnabled && (
                <div style={styles.pinChangeSection}>
                  <h4 style={styles.pinSetupTitle}>Change Transaction PIN</h4>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Current PIN</label>
                    <input
                      type="password"
                      value={changePinForm.currentPin}
                      onChange={(e) => setChangePinForm({...changePinForm, currentPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="Current PIN"
                      maxLength={6}
                      style={styles.pinInputField}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>New PIN</label>
                    <input
                      type="password"
                      value={changePinForm.newPin}
                      onChange={(e) => setChangePinForm({...changePinForm, newPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="New PIN"
                      maxLength={6}
                      style={styles.pinInputField}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Confirm New PIN</label>
                    <input
                      type="password"
                      value={changePinForm.confirmNewPin}
                      onChange={(e) => setChangePinForm({...changePinForm, confirmNewPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="Confirm New PIN"
                      maxLength={6}
                      style={styles.pinInputField}
                    />
                  </div>
                  <button 
                    onClick={changePin} 
                    style={{...styles.pinChangeBtn, opacity: pinLoading ? 0.6 : 1}}
                    disabled={pinLoading}
                  >
                    {pinLoading ? "Changing..." : "Change PIN"}
                  </button>
                </div>
              )}
            </div>

            {/* Account Info Card */}
            <div style={styles.settingsCard}>
              <h3 style={styles.settingsCardTitle}>Account Information</h3>
              <div style={styles.accountInfoGrid}>
                <div style={styles.accountInfoItem}>
                  <span style={styles.accountInfoLabel}>Full Name</span>
                  <span style={styles.accountInfoValue}>{user?.fullName}</span>
                </div>
                <div style={styles.accountInfoItem}>
                  <span style={styles.accountInfoLabel}>Email</span>
                  <span style={styles.accountInfoValue}>{user?.email}</span>
                </div>
                <div style={styles.accountInfoItem}>
                  <span style={styles.accountInfoLabel}>Role</span>
                  <span style={styles.accountInfoValue}>Agent</span>
                </div>
                <div style={styles.accountInfoItem}>
                  <span style={styles.accountInfoLabel}>Float Balance</span>
                  <span style={styles.accountInfoValue}>{formatCurrency(floatBalance)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* PIN Modal */}
      {showPinModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🔐 Enter Transaction PIN</h3>
            <p style={styles.modalSubtitle}>
              {pendingAction === "deposit" 
                ? `Confirm deposit of ₦${Number(depositForm.amount).toLocaleString()} to ${customer?.fullName}`
                : `Confirm withdrawal of ₦${Number(withdrawForm.amount).toLocaleString()} for ${customer?.fullName}`}
            </p>
            <input
              type="password"
              value={actionPin}
              onChange={(e) => setActionPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter PIN"
              maxLength={6}
              style={styles.pinInput}
              autoFocus
            />
            <div style={styles.modalButtons}>
              <button 
                onClick={() => { setShowPinModal(false); setActionPin(""); setPendingAction(null); }} 
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button 
                onClick={handlePinSubmit}
                style={{
                  ...styles.confirmBtn,
                  backgroundColor: pendingAction === "deposit" ? "#28a745" : "#dc3545"
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f4f6f9" },
  loadingScreen: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: "20px" },
  loadingSpinner: { width: "50px", height: "50px", border: "4px solid #e9ecef", borderTopColor: "#28a745", borderRadius: "50%", animation: "spin 1s linear infinite" },
  
  header: { backgroundColor: "#155724", color: "white", padding: "15px 30px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" },
  logo: { margin: 0, fontSize: "22px", fontWeight: "700" },
  agentBadge: { backgroundColor: "#28a745", padding: "4px 12px", borderRadius: "15px", fontSize: "11px", fontWeight: "500" },
  pinWarningBadge: { backgroundColor: "#ffc107", color: "#000", padding: "4px 12px", borderRadius: "15px", fontSize: "11px", fontWeight: "500" },
  headerRight: { display: "flex", alignItems: "center", gap: "20px" },
  welcomeText: { fontSize: "14px", opacity: 0.9 },
  logoutBtn: { padding: "8px 16px", backgroundColor: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "white", borderRadius: "5px", cursor: "pointer", fontSize: "13px" },
  
  nav: { backgroundColor: "white", padding: "0 30px", display: "flex", gap: "5px", borderBottom: "1px solid #e9ecef", overflowX: "auto" },
  navItem: { padding: "15px 20px", border: "none", backgroundColor: "transparent", cursor: "pointer", fontSize: "14px", color: "#666", borderBottom: "3px solid transparent", whiteSpace: "nowrap" },
  navItemActive: { color: "#155724", borderBottomColor: "#28a745", fontWeight: "600" },
  
  main: { padding: "30px", maxWidth: "1000px", margin: "0 auto" },
  
  pinAlert: { backgroundColor: "#fff3cd", border: "1px solid #ffc107", padding: "15px 20px", borderRadius: "8px", marginBottom: "25px", color: "#856404", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" },
  pinAlertBtn: { backgroundColor: "#ffc107", color: "#000", border: "none", padding: "8px 16px", borderRadius: "5px", cursor: "pointer", fontWeight: "600" },
  
  dashboard: { display: "flex", flexDirection: "column", gap: "25px" },
  
  floatCard: { background: "linear-gradient(135deg, #155724 0%, #28a745 100%)", color: "white", borderRadius: "16px", padding: "30px", boxShadow: "0 10px 40px rgba(21, 87, 36, 0.3)" },
  floatCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  floatLabel: { fontSize: "14px", opacity: 0.9 },
  floatStatus: { fontSize: "12px", color: "#90EE90" },
  floatBalance: { fontSize: "40px", fontWeight: "700", marginBottom: "15px" },
  floatInfo: { fontSize: "13px", opacity: 0.8 },
  
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" },
  statCard: { backgroundColor: "white", borderRadius: "12px", padding: "20px", display: "flex", alignItems: "center", gap: "15px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  statIcon: { fontSize: "30px", width: "50px", height: "50px", backgroundColor: "#f0f0f0", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" },
  statInfo: {},
  statValue: { fontSize: "20px", fontWeight: "700", color: "#1a1a2e" },
  statLabel: { fontSize: "12px", color: "#666" },
  
  quickActions: { backgroundColor: "white", borderRadius: "12px", padding: "25px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  sectionTitle: { margin: "0 0 20px", fontSize: "16px", fontWeight: "600", color: "#1a1a2e" },
  actionGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "15px" },
  actionBtn: { display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "25px", border: "none", borderRadius: "12px", cursor: "pointer", fontSize: "15px", fontWeight: "600" },
  actionIcon: { fontSize: "36px" },
  
  recentSection: { backgroundColor: "white", borderRadius: "12px", padding: "25px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
  viewAllBtn: { backgroundColor: "transparent", border: "none", color: "#28a745", cursor: "pointer", fontSize: "13px" },
  
  transactionList: { display: "flex", flexDirection: "column" },
  transactionItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 0", borderBottom: "1px solid #f0f0f0" },
  txLeft: { display: "flex", alignItems: "center", gap: "15px" },
  txIcon: { width: "40px", height: "40px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: "bold" },
  txDescription: { fontWeight: "500", fontSize: "14px", color: "#333" },
  txDate: { fontSize: "12px", color: "#999", marginTop: "3px" },
  txAmount: { fontWeight: "600", fontSize: "15px" },
  
  transactionSection: { maxWidth: "600px", margin: "0 auto" },
  pageTitle: { margin: "0 0 5px", fontSize: "24px", fontWeight: "600", color: "#1a1a2e" },
  pageSubtitle: { margin: "0 0 25px", color: "#666", fontSize: "14px" },
  
  floatWarning: { backgroundColor: "#fff3cd", padding: "15px 20px", borderRadius: "8px", marginBottom: "20px", color: "#856404", fontSize: "14px" },
  
  transactionCard: { backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  
  searchSection: { marginBottom: "25px" },
  formTitle: { margin: "0 0 15px", fontSize: "15px", fontWeight: "600", color: "#333" },
  searchTypeRow: { display: "flex", gap: "20px", marginBottom: "15px" },
  radioLabel: { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" },
  input: { width: "100%", padding: "12px 15px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  inputWrapper: { position: "relative" },
  searchingIndicator: { position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" },
  spinner: { width: "20px", height: "20px", border: "2px solid #ddd", borderTopColor: "#28a745", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  helperText: { fontSize: "12px", color: "#888", marginTop: "6px", marginBottom: "0" },
  
  customerCard: { display: "flex", alignItems: "center", gap: "20px", padding: "20px", backgroundColor: "#f8f9fa", borderRadius: "12px", marginBottom: "25px", border: "2px solid #28a745" },
  customerIcon: { width: "60px", height: "60px", backgroundColor: "#e9ecef", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px" },
  customerDetails: {},
  customerName: { fontSize: "18px", fontWeight: "600", color: "#1a1a2e" },
  customerAccount: { fontSize: "14px", color: "#666", fontFamily: "monospace", marginTop: "3px" },
  customerVerified: { fontSize: "13px", color: "#28a745", fontWeight: "500", marginTop: "5px" },
  
  amountSection: { paddingTop: "20px", borderTop: "1px solid #e9ecef" },
  formGroup: { marginBottom: "20px" },
  label: { display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#333" },
  amountInput: { width: "100%", padding: "15px", border: "2px solid #ddd", borderRadius: "8px", fontSize: "20px", fontWeight: "600", textAlign: "center", boxSizing: "border-box" },
  limitText: { fontSize: "12px", color: "#666", marginTop: "5px", textAlign: "right" },
  actionButton: { width: "100%", padding: "15px", color: "white", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "600", cursor: "pointer" },
  
  transactionsSection: {},
  transactionsCard: { backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  th: { textAlign: "left", padding: "12px 10px", borderBottom: "2px solid #e9ecef", fontWeight: "600", color: "#666" },
  td: { padding: "12px 10px", borderBottom: "1px solid #f0f0f0" },
  typeBadge: { padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "500" },
  
  noData: { textAlign: "center", padding: "40px", color: "#999" },
  
  // Settings styles
  settingsCard: { backgroundColor: "white", borderRadius: "12px", padding: "25px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: "20px" },
  settingsCardTitle: { margin: "0 0 20px", fontSize: "18px", fontWeight: "600", color: "#1a1a2e", borderBottom: "1px solid #e9ecef", paddingBottom: "15px" },
  pinStatusBox: { backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "8px", marginBottom: "20px" },
  pinStatusRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  pinStatusBadge: { padding: "5px 12px", borderRadius: "15px", fontSize: "12px", fontWeight: "600" },
  lockedWarning: { backgroundColor: "#f8d7da", color: "#721c24", padding: "10px", borderRadius: "6px", marginTop: "10px", fontSize: "13px" },
  pinSetupSection: { borderTop: "1px solid #e9ecef", paddingTop: "20px" },
  pinSetupTitle: { margin: "0 0 10px", fontSize: "15px", fontWeight: "600", color: "#333" },
  pinSetupDesc: { color: "#666", fontSize: "13px", marginBottom: "20px" },
  pinInputField: { width: "100%", padding: "12px 15px", border: "2px solid #ddd", borderRadius: "8px", fontSize: "18px", textAlign: "center", letterSpacing: "8px", boxSizing: "border-box" },
  pinSetupBtn: { width: "100%", padding: "12px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: "600", cursor: "pointer" },
  pinChangeSection: { borderTop: "1px solid #e9ecef", paddingTop: "20px" },
  pinChangeBtn: { width: "100%", padding: "12px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: "600", cursor: "pointer" },
  accountInfoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" },
  accountInfoItem: { padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px" },
  accountInfoLabel: { display: "block", fontSize: "12px", color: "#666", marginBottom: "5px" },
  accountInfoValue: { fontSize: "14px", fontWeight: "600", color: "#1a1a2e" },
  
  // Modal styles
  modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { backgroundColor: "white", borderRadius: "16px", padding: "30px", width: "400px", maxWidth: "90%", textAlign: "center" },
  modalTitle: { margin: "0 0 10px", fontSize: "20px" },
  modalSubtitle: { color: "#666", fontSize: "14px", marginBottom: "25px" },
  pinInput: { width: "100%", padding: "15px", fontSize: "24px", textAlign: "center", border: "2px solid #ddd", borderRadius: "8px", letterSpacing: "10px", boxSizing: "border-box" },
  modalButtons: { display: "flex", gap: "15px", marginTop: "25px" },
  cancelBtn: { flex: 1, padding: "12px", backgroundColor: "#f8f9fa", border: "1px solid #ddd", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  confirmBtn: { flex: 1, padding: "12px", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "600" },
  quickSelectBtn: { padding: "8px 16px", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "20px", cursor: "pointer", fontSize: "13px" }
};
